/**
 * Project Store (Zustand)
 * 
 * Central state management for projects, files, and Vite status.
 * Integrates with AWS backend for project creation and message persistence.
 */
import { create } from 'zustand';
import { projectApi, viteApi, messageApi } from '../services/api';
import { cloudProjectApi } from '../services/cloudApi';
import { getAuthToken } from '../utils/auth';

const DEPLOYMENT_MODE = process.env.REACT_APP_DEPLOYMENT_MODE || 'local';
const CLOUD_API_URL = process.env.REACT_APP_CLOUD_API_URL || '';

// Monotonically increasing generation counter for Vite lifecycle.
// Every start/stop bumps this. Async callbacks check their captured generation
// against the current value; if it's stale the callback is a no-op.
let _viteGeneration = 0;

// Debounce timer for loadFiles to prevent rapid flickering of the file tree.
let _loadFilesTimer = null;

// Concurrency guard for setCurrentProject to prevent overlapping calls.
let _setCurrentProjectLock = null;

// Generation counter for project navigation — bumped on every setCurrentProject
// call so stale async continuations from a superseded switch become no-ops.
let _projectSwitchGeneration = 0;

// Streaming guard for loadMessages — queues calls while a workflow is streaming.
let _streamingActive = false;
let _pendingLoadMessages = null; // { projectId } — only the latest queued call matters

function resolveViteUrl(backendReportedUrl) {
    if (DEPLOYMENT_MODE === 'cloud') {
        try {
            const url = new URL(backendReportedUrl);
            return `${CLOUD_API_URL}${url.pathname}`;
        } catch {
            return `${CLOUD_API_URL}/api/vite-preview/`;
        }
    }
    return backendReportedUrl;
}

// NOTE: We no longer cache messages in localStorage (was hitting quota limits).
// Messages are persisted to AWS backend and loaded on demand.
// In-memory cache is still used for the current session.

/**
 * Project store for managing builder state
 */
export const useProjectStore = create((set, get) => ({
    // Project state
    projects: [],
    projectsTotal: 0,
    currentProject: null,
    files: [],
    isLoading: false,
    error: null,

    // Vite state
    viteStatus: null,
    viteUrl: null,
    viteLoading: false,
    viteLoadingMessage: null,

    // Agent execution state
    isExecuting: false,
    executionMessages: [],
    
    // Chat messages per project (projectId -> messages[])
    // In-memory cache only - messages are persisted to AWS backend
    projectMessages: {},
    
    // AWS sync state
    awsSyncError: null,

    // When true, .threedium is listed in the Monaco file tree (superadmin or REACT_APP_DEPLOYMENT_MODE=local; see CodeEditorPanel)
    showHiddenFiles: false,
    setShowHiddenFiles: (value) => set({ showHiddenFiles: value }),

    // Actions

    /**
     * Load projects from AWS backend with optional filters
     * 
     * @param {Object} options - Query options
     * @param {string|null} options.folderId - Filter by folder
     * @param {string|null} options.search - Search query
     * @param {number} options.page - Page number
     * @param {number} options.pageSize - Items per page
     */
    loadProjects: async (options = {}) => {
        set({ isLoading: true, error: null });
        try {
            // Load projects from AWS with filters
            const data = await cloudProjectApi.list(options);
            set({ 
                projects: data.projects || [], 
                projectsTotal: data.total || 0,
                isLoading: false 
            });
            return data;
        } catch (error) {
            console.error('[Projects] Failed to load from AWS:', error);
            set({ error: error.message, isLoading: false, projects: [] });
            throw error;
        }
    },

    /**
     * Create new project via AWS backend
     * 
     * 1. Create project on AWS (with template + git)
     * 2. Clone project to local backend
     * 3. Start Vite dev server
     * 
     * @param {string} name - Project name
     * @param {string} template - Template name ('vite-react' or 'unlimited3d')
     * @param {string} description - Project description
     * @param {string} solutionId - Optional Threedium solution ID (for unlimited3d template)
     * @param {boolean} autoStartVite - Whether to auto-start Vite (default true)
     */
    createProject: async (name, template = 'vite-react', description = '', solutionId = null, autoStartVite = true) => {
        set({ isLoading: true, error: null });
        try {
            // Capture old project ID BEFORE updating state so we stop the right Vite
            const oldProject = get().currentProject;

            // Step 1: Create project on AWS
            console.log('[Project] Creating project on AWS...');
            const awsProject = await cloudProjectApi.createFromTemplate({
                name,
                template,
                description,
                solution_id: solutionId,
            });
            
            console.log('[Project] AWS project created:', awsProject.id);
            
            // Step 2: Clone from AWS to local backend
            console.log('[Project] Cloning to local backend...');
            const authToken = getAuthToken();
            const cloneResult = await projectApi.cloneFromAws(awsProject.id, authToken);
            const project = {
                ...cloneResult.project,
                aws_project_id: awsProject.id,
                awsProject, // Keep AWS project info
            };
            
            console.log('[Project] Local project created:', project.id);

            // Stop old project's Vite BEFORE updating currentProject
            if (autoStartVite && oldProject) {
                await get().stopVite(oldProject.id).catch(() => {});
            }
            
            set(state => ({
                projects: [...state.projects, project],
                currentProject: project,
                isLoading: false,
            }));
            
            // Load files for new project (immediate, no debounce)
            await get().loadFiles(true);
            
            if (autoStartVite) {
                await get().startVite();
            }
            
            return project;
        } catch (error) {
            console.error('[Project] Creation error:', error);
            set({ error: error.message, isLoading: false });
            throw error;
        }
    },
    
    /**
     * Set current project - clones from AWS if not already local
     * 
     * @param {string} projectId - The AWS project ID (or local ID)
     */
    setCurrentProject: async (projectId) => {
        // Concurrency guard: if another setCurrentProject is in flight, wait for it
        // then re-check whether the project is already current.
        if (_setCurrentProjectLock) {
            try { await _setCurrentProjectLock; } catch { /* ignore */ }
            const cur = get().currentProject;
            if (cur && (cur.aws_project_id === projectId || cur.id === projectId)) {
                return cur;
            }
        }

        let resolve;
        _setCurrentProjectLock = new Promise(r => { resolve = r; });

        const gen = ++_projectSwitchGeneration;

        set({ isLoading: true, error: null });
        
        try {
            // Capture old project BEFORE any state changes
            const oldProject = get().currentProject;

            // Already loaded as current?
            if (oldProject && (oldProject.aws_project_id === projectId || oldProject.id === projectId)) {
                console.log('[Project] Project already loaded as current:', projectId);
                set({ isLoading: false });
                return oldProject;
            }
            
            // Find project in local list - check both id and aws_project_id
            let awsProject = get().projects.find(
                p => p.id === projectId || p.aws_project_id === projectId
            );
            
            // If not in local list, try fetching from AWS directly
            if (!awsProject) {
                console.log('[Project] Project not in local list, fetching from AWS:', projectId);
                try {
                    awsProject = await cloudProjectApi.get(projectId);
                } catch (fetchError) {
                    console.error('[Project] Failed to fetch from AWS:', fetchError);
                    throw new Error('Project not found');
                }
            }
            
            if (!awsProject) {
                throw new Error('Project not found');
            }

            if (gen !== _projectSwitchGeneration) {
                console.log('[Project] Superseded by newer project switch, aborting');
                set({ isLoading: false });
                return null;
            }
            
            // Get the AWS project ID (might be in id or aws_project_id depending on source)
            const awsProjectId = awsProject.aws_project_id || awsProject.id;
            
            // Clone from AWS to local backend
            console.log('[Project] Cloning project from AWS:', awsProjectId);
            const authToken = getAuthToken();
            const cloneResult = await projectApi.cloneFromAws(awsProjectId, authToken);

            if (gen !== _projectSwitchGeneration) {
                console.log('[Project] Superseded by newer project switch after clone, aborting');
                set({ isLoading: false });
                return null;
            }
            
            const project = {
                ...cloneResult.project,
                aws_project_id: awsProjectId,
                thumbnail: awsProject.thumbnail,
                display_name: awsProject.display_name,
            };
            
            console.log('[Project] Project cloned locally:', project.id);

            // Stop the OLD project's Vite BEFORE switching currentProject
            if (oldProject) {
                await get().stopVite(oldProject.id).catch(() => {});
            }
            
            set({ 
                currentProject: project, 
                isLoading: false 
            });
            
            await get().loadFiles(true);
            
            // Load messages via local backend gateway (L-58: properly awaited)
            await get().loadMessages(project.id);
            
            // Auto-start Vite (only if this switch is still current)
            if (gen === _projectSwitchGeneration) {
                await get().startVite();
            }
            
            return project;
        } catch (error) {
            console.error('[Project] Failed to set current project:', error);
            set({ error: error.message, isLoading: false });
            throw error;
        } finally {
            _setCurrentProjectLock = null;
            resolve();
        }
    },

    /**
     * Load files for current project (debounced to prevent rapid flickering)
     */
    loadFiles: (immediate = false) => {
        if (_loadFilesTimer) clearTimeout(_loadFilesTimer);

        const doLoad = async () => {
            const project = get().currentProject;
            if (!project) return;

            try {
                const data = await projectApi.listFiles(project.id, '', { showHidden: get().showHiddenFiles });
                set({ files: data.files });
            } catch (error) {
                console.error('Failed to load files:', error);
                set({ error: error.message });
            }
        };

        if (immediate) {
            return doLoad();
        }
        return new Promise((resolve) => {
            _loadFilesTimer = setTimeout(() => doLoad().then(resolve), 300);
        });
    },

    /**
     * Read file content
     */
    readFile: async (path) => {
        const project = get().currentProject;
        if (!project) throw new Error('No project selected');

        const data = await projectApi.readFile(project.id, path);
        return data.content;
    },

    /**
     * Write file content
     */
    writeFile: async (path, content) => {
        const project = get().currentProject;
        if (!project) throw new Error('No project selected');

        await projectApi.writeFile(project.id, path, content);
        await get().loadFiles();
    },

    /**
     * Delete file
     */
    deleteFile: async (path) => {
        const project = get().currentProject;
        if (!project) throw new Error('No project selected');

        await projectApi.deleteFile(project.id, path);
        await get().loadFiles();
    },

    /**
     * Delete project
     */
    deleteProject: async (projectId) => {
        const project = get().projects.find(p => p.id === projectId);

        // Stop Vite for the project being deleted (fire-and-forget)
        get().stopVite(projectId).catch(() => {});
        
        // Delete from local
        await projectApi.delete(projectId);
        
        // Delete from AWS if linked
        if (project?.aws_project_id) {
            try {
                await cloudProjectApi.delete(project.aws_project_id);
            } catch (error) {
                console.warn('Failed to delete AWS project:', error);
            }
        }
        
        // Clean up local cache
        const newMessages = { ...get().projectMessages };
        delete newMessages[projectId];
        
        set(state => ({
            projects: state.projects.filter(p => p.id !== projectId),
            currentProject: state.currentProject?.id === projectId ? null : state.currentProject,
            projectMessages: newMessages,
        }));
    },

    // ===== Message Management (via local backend gateway) =====

    /**
     * Mark streaming as active/inactive. While active, loadMessages calls are
     * queued and only the latest is executed once streaming ends.
     */
    setStreaming: (active) => {
        _streamingActive = active;
        if (!active && _pendingLoadMessages) {
            const { projectId } = _pendingLoadMessages;
            _pendingLoadMessages = null;
            get().loadMessages(projectId);
        }
    },

    /**
     * Load messages for a project via the local backend gateway.
     * The local backend fetches from AWS (or its cache) and returns messages
     * with camelCase field names.
     *
     * Skips the fetch while a workflow is actively streaming and queues
     * the request for replay once streaming completes.
     *
     * @param {string} localProjectId - Local project ID
     */
    loadMessages: async (localProjectId) => {
        if (_streamingActive) {
            console.log('[Messages] Streaming active, queuing loadMessages for', localProjectId);
            _pendingLoadMessages = { projectId: localProjectId };
            return get().projectMessages[localProjectId] || [];
        }

        try {
            const result = await messageApi.getMessages(localProjectId);
            const messages = (result.messages || []).map(m => ({
                id: m.id,
                role: m.role,
                content: m.content,
                attachments: m.attachments || [],
                contentBlocks: m.contentBlocks || [],
                commitShaBefore: m.commitShaBefore,
                commitShaAfter: m.commitShaAfter,
                stopped: m.stopped || false,
                createdAt: m.createdAt,
            }));

            const newMessages = {
                ...get().projectMessages,
                [localProjectId]: messages,
            };
            set({ projectMessages: newMessages, awsSyncError: null });

            return messages;
        } catch (error) {
            console.error('Failed to load messages:', error);
            set({ awsSyncError: error.message });
            return [];
        }
    },

    /**
     * Get messages for a specific project (from in-memory cache)
     */
    getProjectMessages: (projectId) => {
        return get().projectMessages[projectId] || [];
    },

    /**
     * Set messages for a specific project (used by BuilderWorkspace)
     */
    setProjectMessages: (projectId, messages) => {
        const newMessages = {
            ...get().projectMessages,
            [projectId]: messages,
        };
        set({ projectMessages: newMessages });
    },
    
    // ===== Git Operations =====
    
    /**
     * Get current AWS commit SHA for a project (used for undo tracking)
     * This gets the HEAD SHA directly from AWS - the source of truth
     */
    getCurrentCommitSha: async () => {
        const project = get().currentProject;
        if (!project?.aws_project_id) return null;
        
        try {
            // Get SHA directly from AWS - the source of truth
            const gitInfo = await cloudProjectApi.getGitInfo(project.aws_project_id);
            return gitInfo?.head_sha || null;
        } catch (error) {
            console.error('Failed to get AWS git info:', error);
            return null;
        }
    },
    
    /**
     * Commit and push changes to AWS
     */
    commitAndPushChanges: async (message = 'Agent changes') => {
        const project = get().currentProject;
        if (!project?.aws_project_id) return null;
        
        try {
            const authToken = getAuthToken();
            const result = await projectApi.pushToAws(project.id, message, authToken);
            return result;
        } catch (error) {
            console.error('Failed to push changes:', error);
            set({ awsSyncError: error.message });
            return null;
        }
    },
    
    /**
     * Undo to a previous message (revert project and delete later messages)
     * @param {string} messageId - Message ID to revert to
     */
    undoToMessage: async (messageId) => {
        const project = get().currentProject;
        if (!project?.aws_project_id) return { success: false, error: 'No AWS project' };
        
        // Find the message in local cache (which is synced from AWS)
        const messages = get().projectMessages[project.id] || [];
        const message = messages.find(m => m.id === messageId);
        
        if (!message?.commitShaBefore) {
            return { success: false, error: 'No commit SHA to revert to. The project may not have been synced to AWS when this message was sent.' };
        }
        
        try {
            // Revert via local backend (which syncs with AWS and invalidates cache)
            const authToken = getAuthToken();
            const result = await projectApi.revertToCommit(
                project.id,
                message.commitShaBefore,
                messageId,
                authToken
            );
            
            if (result.invalidate_cache || result.status === 'success') {
                // Clear local cache and reload via local backend (which re-fetches from AWS)
                const newMessages = {
                    ...get().projectMessages,
                    [project.id]: [],
                };
                set({ projectMessages: newMessages });

                await get().loadMessages(project.id);
            }
            
            // Reload files to reflect reverted state (immediate)
            await get().loadFiles(true);
            
            return { success: true, ...result };
        } catch (error) {
            console.error('Undo failed:', error);
            return { success: false, error: error.message };
        }
    },
    
    /**
     * Handle cache invalidation event from backend
     * Called when backend invalidates message cache (e.g., after undo)
     * 
     * @param {string} projectId - Local project ID
     * @param {string} reason - Reason for invalidation (e.g., "undo")
     */
    handleCacheInvalidation: async (projectId, reason) => {
        const project = get().currentProject;
        if (project?.id === projectId) {
            console.log(`[Cache] Invalidating messages for ${projectId} (reason: ${reason})`);

            const newMessages = {
                ...get().projectMessages,
                [projectId]: [],
            };
            set({ projectMessages: newMessages });

            await get().loadMessages(projectId);
        }
    },

    // ===== Vite actions =====
    // All state mutations go through _viteGeneration so stale async callbacks
    // (from a stop-all that resolves after a newer start) never clobber fresh state.
    
    /**
     * Check if npm is available
     */
    checkNpm: async () => {
        try {
            const result = await viteApi.checkNpm();
            return result;
        } catch (error) {
            return { available: false, info: error.message };
        }
    },

    /**
     * Start Vite dev server with loading states.
     * Bumps the generation counter so any in-flight stop becomes a no-op.
     */
    startVite: async () => {
        const project = get().currentProject;
        if (!project) throw new Error('No project selected');

        const gen = ++_viteGeneration;

        set({ viteLoading: true, viteLoadingMessage: 'Checking npm availability...' });

        try {
            const npmCheck = await viteApi.checkNpm();
            if (gen !== _viteGeneration) return { running: false, error: 'superseded' };
            if (!npmCheck.available) {
                set({ 
                    viteStatus: { running: false, error: npmCheck.info },
                    viteUrl: null,
                    viteLoading: false,
                    viteLoadingMessage: null,
                    error: npmCheck.info
                });
                return { running: false, error: npmCheck.info };
            }
            
            set({ viteLoadingMessage: 'Installing dependencies (this may take a minute)...' });
            
            const status = await viteApi.start(project.id);

            if (gen !== _viteGeneration) return status;
            
            if (status.running) {
                set({ 
                    viteStatus: status, 
                    viteUrl: resolveViteUrl(status.url),
                    viteLoading: false,
                    viteLoadingMessage: null
                });
            } else {
                set({ 
                    viteStatus: status,
                    viteUrl: null,
                    viteLoading: false,
                    viteLoadingMessage: null,
                    error: status.error || 'Failed to start preview'
                });
            }
            return status;
        } catch (error) {
            if (gen !== _viteGeneration) return { running: false, error: 'superseded' };
            set({ 
                error: error.message,
                viteLoading: false,
                viteLoadingMessage: null
            });
            throw error;
        }
    },

    /**
     * Stop Vite dev server for a specific project.
     * Bumps the generation counter and clears UI immediately.
     * @param {string} [projectId] - Project to stop. Falls back to currentProject.
     */
    stopVite: async (projectId) => {
        const id = projectId || get().currentProject?.id;
        if (!id) return;

        const gen = ++_viteGeneration;
        set({ viteStatus: null, viteUrl: null, viteLoading: false, viteLoadingMessage: null });

        try {
            await viteApi.stop(id);
        } catch (error) {
            console.error('Failed to stop Vite:', error);
        }
    },

    /**
     * Stop ALL running Vite dev servers (used on navigation away).
     * Clears UI state IMMEDIATELY (synchronously) so the iframe unmounts
     * before the async backend call completes. The generation counter
     * ensures a later startVite won't be clobbered when the response arrives.
     */
    stopAllVite: async () => {
        const gen = ++_viteGeneration;
        set({ viteStatus: null, viteUrl: null, viteLoading: false, viteLoadingMessage: null });

        try {
            await viteApi.stopAll();
        } catch (error) {
            console.error('Failed to stop all Vite:', error);
        }
    },

    /**
     * Check Vite status.
     * Uses the project-switch generation counter so a stale poll from a
     * previous project never overwrites the current project's state.
     */
    checkViteStatus: async () => {
        const project = get().currentProject;
        if (!project) return;

        const gen = _projectSwitchGeneration;

        try {
            const status = await viteApi.status(project.id);
            if (gen !== _projectSwitchGeneration) return;
            set({
                viteStatus: status,
                viteUrl: status.running ? resolveViteUrl(status.url) : null,
            });
        } catch (error) {
            if (gen !== _projectSwitchGeneration) return;
            set({ viteStatus: null, viteUrl: null });
        }
    },

    // ===== Execution state =====

    setExecuting: (isExecuting) => set({ isExecuting }),

    addMessage: (message) => set(state => ({
        executionMessages: [...state.executionMessages, message],
    })),

    clearMessages: () => set({ executionMessages: [] }),

    clearError: () => set({ error: null }),
}));

export default useProjectStore;
